/**
 * @file The member sidebar's Messages badge (host-messaging-tech-design §6.3, R6).
 *
 * `guestUnread`: conversations with a host message the member hasn't read.
 * `hasThreads`: whether they have any conversation as a guest at all; a
 * travel host sees "Messages" only then (R6).
 *
 * The same two writers as the inbox badge (hostInboxSlice):
 *  - `fetchConversationSummary`, run by hooks/useGuestMessagesBadge.js on
 *    mount, on window focus, and when the bell's polled count rises (every
 *    host reply raises a bell item);
 *  - `setGuestUnread`, from the /messages list's `unread_total`, and refetched
 *    after every read, so the badge drops when the server says so.
 *
 * Not persisted, and reset on logout (store/index.jsx `resetSlices`).
 *
 * Plain .js: no JSX.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Lazy-import, as hostInboxSlice does: store → slice → api → callAzureFunctions → store.
const getApi = () => import('../../api/conversations');

const toCount = (value) => (Number.isFinite(value) && value > 0 ? Math.floor(value) : 0);

/** `GET user/conversations/summary`. A failure keeps the last values: the badge is a hint. */
export const fetchConversationSummary = createAsyncThunk('conversation/fetchSummary', async () => {
  const { getConversationsSummary } = await getApi();
  const summary = await getConversationsSummary();
  return { unread: toCount(summary?.unread), hasThreads: Boolean(summary?.has_threads) };
});

export const initialState = {
  guestUnread: 0,
  hasThreads: false
};

const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    /** `unread_total` from the /messages list. */
    setGuestUnread: (state, action) => {
      state.guestUnread = toCount(action.payload);
      if (state.guestUnread > 0) state.hasThreads = true;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConversationSummary.fulfilled, (state, action) => {
      state.guestUnread = action.payload.unread;
      state.hasThreads = action.payload.hasThreads;
    });
  }
});

export const { setGuestUnread } = conversationSlice.actions;
export const selectGuestUnread = (state) => state.conversation?.guestUnread ?? 0;
export const selectHasGuestThreads = (state) => Boolean(state.conversation?.hasThreads);
export default conversationSlice.reducer;
